import React, { useState, useEffect, useRef } from 'react';
import { tagService, Tag } from '../../services/tagService';
import './TagSelector.css';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  availableTags?: Tag[];
  placeholder?: string;
}

const DEFAULT_COLORS = [
  '#FF5733', '#33FF57', '#3357FF', '#FF33F5', '#F5FF33',
  '#33FFF5', '#FF3366', '#6633FF', '#FF9933', '#33FF99'
];

const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onChange,
  availableTags: providedTags,
  placeholder = 'Select or create tags...'
}) => {
  const [availableTags, setAvailableTags] = useState<Tag[]>(providedTags || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(DEFAULT_COLORS[0]);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load tags if not provided
  useEffect(() => {
    if (!providedTags) {
      loadTags();
    } else {
      setAvailableTags(providedTags);
    }
  }, [providedTags]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const loadTags = async () => {
    try {
      const response = await tagService.getAllTags();
      if (response.success && response.tags) {
        setAvailableTags(response.tags);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const filteredTags = availableTags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedTags.includes(tag.name)
  );

  const handleTagSelect = (tagName: string) => {
    if (!selectedTags.includes(tagName)) {
      onChange([...selectedTags, tagName]);
    }
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleTagRemove = (tagName: string) => {
    onChange(selectedTags.filter(t => t !== tagName));
  };

  const handleCreateTag = async () => {
    const trimmedName = newTagName.trim();
    if (!trimmedName || isCreatingTag) return;

    // Check if tag already exists
    if (availableTags.some(t => t.name.toLowerCase() === trimmedName.toLowerCase())) {
      // Just select it instead of creating
      handleTagSelect(trimmedName);
      setNewTagName('');
      return;
    }

    setIsCreatingTag(true);
    try {
      const response = await tagService.createTag(trimmedName, newTagColor);
      if (response.success && response.tag) {
        setAvailableTags([...availableTags, response.tag]);
        onChange([...selectedTags, response.tag.name]);
        setNewTagName('');
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('Error creating tag:', error);
    } finally {
      setIsCreatingTag(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTagName.trim()) {
      e.preventDefault();
      handleCreateTag();
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSearchTerm('');
      setNewTagName('');
    }
  };

  return (
    <div className="tag-selector-container" ref={containerRef}>
      {/* Selected Tags Display */}
      {selectedTags.length > 0 && (
        <div className="tag-selector-selected">
          {selectedTags.map((tagName) => {
            const tag = availableTags.find(t => t.name === tagName);
            return (
              <span
                key={tagName}
                className="tag-selector-chip"
                style={{ backgroundColor: tag?.color_code || '#666666' }}
              >
                <span className="tag-selector-chip-name">{tagName}</span>
                <button
                  className="tag-selector-chip-remove"
                  onClick={() => handleTagRemove(tagName)}
                  title="Remove tag"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Input Field */}
      <div className="tag-selector-input-wrapper">
        <input
          type="text"
          className="tag-selector-input"
          placeholder={placeholder}
          value={searchTerm || newTagName}
          onChange={(e) => {
            const value = e.target.value;
            setSearchTerm(value);
            setNewTagName(value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
        />
        
        {/* Dropdown */}
        {showDropdown && (
          <div className="tag-selector-dropdown">
            {/* Existing Tags */}
            {filteredTags.length > 0 && (
              <div className="tag-selector-section">
                <div className="tag-selector-section-title">Existing Tags</div>
                <div className="tag-selector-tags-list">
                  {filteredTags.map((tag) => (
                    <button
                      key={tag.name}
                      className="tag-selector-option"
                      onClick={() => handleTagSelect(tag.name)}
                    >
                      <span
                        className="tag-selector-option-color"
                        style={{ backgroundColor: tag.color_code }}
                      ></span>
                      <span className="tag-selector-option-name">{tag.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Create New Tag */}
            {newTagName.trim() && !availableTags.some(t => t.name.toLowerCase() === newTagName.trim().toLowerCase()) && (
              <div className="tag-selector-section">
                <div className="tag-selector-section-title">Create New Tag</div>
                <div className="tag-selector-create-form">
                  <div className="tag-selector-create-preview">
                    <span
                      className="tag-selector-chip"
                      style={{ backgroundColor: newTagColor }}
                    >
                      {newTagName.trim()}
                    </span>
                  </div>
                  <div className="tag-selector-color-picker">
                    {DEFAULT_COLORS.map((color) => (
                      <button
                        key={color}
                        className={`tag-selector-color-option ${newTagColor === color ? 'selected' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setNewTagColor(color)}
                        title={color}
                      />
                    ))}
                  </div>
                  <button
                    className="tag-selector-create-btn"
                    onClick={handleCreateTag}
                    disabled={isCreatingTag || !newTagName.trim()}
                  >
                    {isCreatingTag ? 'Creating...' : 'Create Tag'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TagSelector;

